import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ShowreelModal } from "@/components/wve/ShowreelModal";
import { EnquiryModal } from "@/components/wve/EnquiryModal";

const lines = [
  { text: "WE MAKE", accent: false },
  { text: "BRANDS", accent: false },
  { text: "MOVE.", accent: true },
];

const tape = ["VIDEO PRODUCTION", "LIVE STREAMS", "EVENT PRODUCTION", "ARTIST SHOWREELS", "BUSINESS PROMOS", "HYBRID BROADCAST"];

let introSeen = false;

export const Hero = () => {
  const ref = useRef(null);
  const base = useRef(introSeen ? 0 : 2.35).current;
  const [reelOpen, setReelOpen] = useState(false);
  const [enquireOpen, setEnquireOpen] = useState(false);
  useEffect(() => { introSeen = true; }, []);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex flex-col justify-end overflow-hidden pt-24 bg-[#0A0A0A] text-white" data-testid="hero-section">
      <div className="absolute inset-0">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          poster="https://static.wixstatic.com/media/1fe187_3633287a6f614c5aba2a841f9cc5f1b6~mv2_d_2048_1462_s_2.jpg/v1/fill/w_1600,h_900,al_c,q_88/hero.jpg"
          autoPlay
          muted
          loop
          playsInline
          data-testid="hero-video-bg"
        >
          <source src="/hero-bg.mp4" type="video/mp4" />
          <source src="/hero-bg.webm" type="video/webm" />
        </video>
        <div className="absolute inset-0 bg-black/65" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-black/40" />
      </div>

      <div className="relative z-10 max-w-[1600px] w-full mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-10 items-end pb-12">
        <div className="lg:col-span-9">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: base + 0.2, duration: 0.7 }}
            className="text-sm uppercase tracking-[0.3em] font-semibold text-[#E80042] mb-6"
          >
            WVE Studios — Video, Photography & Event Production, Essex
          </motion.p>
          <h1 className="font-display uppercase leading-[0.92] text-[16vw] sm:text-[12.5vw] lg:text-[9rem] tracking-tight">
            {lines.map((line, i) => (
              <span key={line.text} className="block overflow-hidden">
                <motion.span
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: base + 0.35 + i * 0.16, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                  className={`block ${line.accent ? "text-[#E80042]" : ""}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: base + 1, duration: 0.8 }}
            className="mt-8 max-w-xl text-lg sm:text-xl leading-relaxed text-white/75"
          >
            A central hub for creative digital media & event production. Trusted by BAFTA, Shell, Ferrari & F1 — with 20+ years of combined industry experience.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: base + 1.15, duration: 0.8 }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <button
              onClick={() => setReelOpen(true)}
              data-testid="hero-watch-showreel"
              className="group bg-[#E80042] text-white font-grotesk font-bold text-lg px-9 py-4 rounded-full hover:bg-white hover:text-[#0A0A0A] transition-colors duration-300 active:scale-95 inline-flex items-center gap-3"
            >
              <span className="w-0 h-0 border-y-[7px] border-y-transparent border-l-[11px] border-l-current inline-block" />
              Watch the Showreel
            </button>
            <button
              onClick={() => setEnquireOpen(true)}
              data-testid="hero-cta-start-project"
              className="border border-white/40 text-white font-grotesk font-bold text-lg px-9 py-4 rounded-full hover:bg-white hover:text-[#0A0A0A] transition-colors duration-300 active:scale-95"
            >
              Start a Project →
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: base + 1.4, duration: 0.6, type: "spring" }}
          className="hidden lg:flex lg:col-span-3 justify-end pb-6"
        >
          <div className="w-32 h-32">
            <svg viewBox="0 0 100 100" className="spin-slow w-full h-full">
              <defs>
                <path id="circlePath" d="M 50, 50 m -37, 0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
              </defs>
              <text fontSize="10.5" fill="#FFFFFF" fontFamily="Outfit" fontWeight="600" letterSpacing="2.5">
                <textPath href="#circlePath">EST. 2018 • WICKFORD • ESSEX •</textPath>
              </text>
              <circle cx="50" cy="50" r="8" fill="#E80042" />
            </svg>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: base + 1.3, duration: 0.8 }}
        className="relative z-10 -rotate-1 -mx-4 mb-[-1px]"
        data-testid="hero-tape-marquee"
      >
        <Marquee speed={50} autoFill gradient={false} className="bg-[#E80042] py-3">
          {tape.map((t) => (
            <span key={t} className="font-grotesk font-bold text-white uppercase tracking-[0.15em] text-sm sm:text-base mx-6 flex items-center gap-6">
              {t} <span className="text-[#0A0A0A]">✺</span>
            </span>
          ))}
        </Marquee>
      </motion.div>

      <ShowreelModal open={reelOpen} onClose={() => setReelOpen(false)} />
      <EnquiryModal open={enquireOpen} onClose={() => setEnquireOpen(false)} context="New Project" />
    </section>
  );
};
